import React, { useState, useRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Tabs, { TabPane } from 'rc-tabs';
import TabContent from 'rc-tabs/lib/TabContent';
import ScrollableInkTabBar from 'rc-tabs/lib/ScrollableInkTabBar';
import {
    FicComboBox,
    FicInput,
    FicTextArea,
    FicDatePickerSI,
    FicNumber,
} from "../../Utils/FormInputComponents/index";
import DialogModal from "../../components/DialogModal";
import "../../../../../style/autoVentas/VentasForm.css";
import GenBtn from "../../components/GenBtn";
import { validar } from "../../components/ValidarNit";
import { withRouter } from "react-router";
import styled from "styled-components";
import LoadMask from "../../Utils/LoadMask/LoadMask";
import TablaPolizas from "./TablaPolizaEditar";

const DialogContent = styled.div`
    height: 205px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
`;

const schema = yup.object({
    fecha_venta: yup.string().required("La fecha es requerida"),
    vendedor: yup.mixed().required("El vendedor es requerido"),
    comprador_nombre: yup.string().required("El comprador es requerido"),
    comprador_dpi: yup
        .number()
        .typeError("Debe de ser un numero")
        .test("dpi-validation", "El dpi no es valido", function (value) {
            return validar.valCui(value.toString())[0].validar;
        })
        .required("El numero de dpi es requerido"),
    comprador_domicilio: yup.string().required("El domicilio es requerido"),
    comprador_departamento: yup
        .string()
        .required("El departamento es requerido"),
    comprador_profesion: yup.string().required("La profesion es requerida"),
    comprador_num_telefono: yup
        .number()
        .typeError("Debe de ser un numero")
        .required("El numero de telefono es requerido"),
    comprador_nit: yup
        .string()
        .test("dpi-validation", "El NIT no es valido", function (value) {
            return validar.valNit(value);
        })
        .required("El nit es requerido"),
    comprador_edad: yup
        .number()
        .positive("La edad debe ser mayor a 0")
        .typeError("Debe de ser un numero")
        .required("La edad es requerida"),
    comprador_estado_civil: yup
        .string()
        .required("El estado civil es requerido"),
    valor_venta: yup
        .number()
        .positive("El valor de venta debe ser mayor a 0")
        .typeError("Debe de ser un numero")
        .required("El valor de venta es requerido"),
});

function VendedoresFormEditar(props) {
    const {
        datosDeVenta,
        editarVenta,
        listarVendedores,
        reporteVentaDeVehiculos,
        history,
        parametrosEmpresa,
        datos,
        eliminarLinea,
        listarCuentas,
        registroPolizaDetalle,
        poliza,
        loader,
        diferencia
    } = props;

    const dialogRef = useRef(null);
    const urlParams = props.match.params;
    const empresa = urlParams.empresa;
    const ventaId = urlParams.ventaId;

    const [idPoliza, setIDPoliza] = useState('')
    const [key, setKey] = useState('VENTA_TAB');
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const parts = dateString.split("-");
        if (parts.length !== 3) return dateString;
        
        const [dd, mm, yyyy] = parts;
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const {
        control,
        handleSubmit,
        formState: {  },
    } = useForm({
        mode: "all",
        resolver: yupResolver(schema),
        defaultValues: async () => {
            try {
                const [formData] = await Promise.all([
                    datosDeVenta(ventaId, datos),
                ]);
                
                if (datos.contabilidad === 'S'){
                    if (formData.poliza[0].encabezado){
                        setIDPoliza(formData.poliza[0].encabezado)
                    }
                }
                
                if (formData.fecha_venta) {
                    formData.fecha_venta = formatDate(formData.fecha_venta);
                }
                return formData;
            } catch (err) {
                return {};
            }
        },
    });

    useEffect(() => {
        parametrosEmpresa();
    }, []);

    const onSubmit = async (data) => {
        const {
            codigo,
            placa,
            tipo_carro,
            modelo,
            linea,
            num_serie,
            carro,
            ...filteredData
        } = data;

        const dataCompleto = {
            ...filteredData,     
            poliza,
            datos
        }

        let shouldShowModal = await editarVenta(dataCompleto, ventaId);
        if (shouldShowModal) {
            dialogRef.current.showModal();
        }
    }

    const closeModal = () => {
        resetModal({
            cuenta: null,
            debe: "",
            haber: ""
        });
        setIsModalOpen(false);
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const modalForm = useForm({
        defaultValues: {
            cuenta: { label: '', value: '' },
            debe: "0.00",
            haber: "0.00"
        }
    });

    const { 
        handleSubmit: handleModalSubmit,
        control: controlModal,
        reset: resetModal
    } = modalForm;

    
    const agregarLinea = (data) => {
        if (!data.cuenta) {
            Swal.fire({
                title: 'Ventas',
                text: 'Debe seleccionar una cuenta',
                type: 'error',
            })
            return;
        }
            
        if ((parseFloat(data.debe) || 0) === 0 && (parseFloat(data.haber) || 0) === 0) {
            Swal.fire({
                title: 'Ventas',
                text: 'Debe ingresar un valor en debe o haber',
                type: 'error',
            })
            return;
        }

        if ((parseFloat(data.debe) || 0) !== 0 && (parseFloat(data.haber) || 0) !== 0) {
            Swal.fire({
                title: 'Ventas',
                text: 'Solo debe ingresar un valor en debe o haber, no ambos',
                type: 'error',
            });
            return;
        }

        const nuevaLinea = {
            id: Date.now(),
            cuenta: data.cuenta.label,
            cuenta_id: data.cuenta.value,
            debe: parseFloat(data.debe) || 0,
            haber: parseFloat(data.haber) || 0,
            idPoliza: idPoliza
        };
        
        registroPolizaDetalle(nuevaLinea);
        closeModal();
    };
    
    return (
        <React.Fragment>
            <br />
            <div className="py-1">
                <div className="mb-1">
                    <div className="p-0 px-3 pt-0">
                    {(datos.contabilidad === 'S') &&
                        <form
                            encType="multipart/form-data"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                        <Tabs
                            destroyInactiveTabPane={true}
                            activeKey={key}
                            onChange={(k) => setKey(k)}
                            defaultActiveKey="PRINCIPAL_TOP"
                            tabBarPosition="top"
                            renderTabBar={() => <ScrollableInkTabBar />}
                            renderTabContent={() => <TabContent />}
                        >
                            <TabPane tab="Venta" key="VENTA_TAB">
                                <div className="py-2 px-3">
                                    <div className="mb-4 text-white mb-1">
                                        <div className="p-0 pt-4 d-flex flex-column flex-md-row">
                                            <div className="d-flex flex-column flex-1 mx-4">
                                                <h3
                                                    className="p-2  title rounded shadow"
                                                    style={{
                                                        background:
                                                            "linear-gradient(to top right, #790021, #2d2d2d)",
                                                    }}
                                                >
                                                    Modificar Venta
                                                </h3>
                                                <br />
                                                <div className="d-flex flex-row justify-content-start mb-2">
                                                    <GenBtn
                                                        BtnColor="#787878"
                                                        style={{ width: "100px", height: "40px" }}
                                                        onPress={(e) => {
                                                            history.push(`/auto_ventas/${empresa}/ventas`);
                                                        }}
                                                    >
                                                        Regresar
                                                    </GenBtn>
                                                </div>
                                                <LoadMask loading={loader} color="#bd0034" blur={true}>
                                                    <div style={{ color: "white" }}>DATOS DEL VEHICULO</div>
                                                    <div className="ventas-form__auto-data">
                                                        <div className="ventas-form__inputs-container">
                                                            <FicInput
                                                                label="Placa"
                                                                name="placa"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                            <FicInput
                                                                label="Codigo"
                                                                name="codigo"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                            <FicInput
                                                                label="Tipo de carro"
                                                                name="tipo_carro"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                            <FicInput
                                                                label="Modelo"
                                                                name="modelo"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                            <FicInput
                                                                label="Linea"
                                                                name="linea"
                                                                control={control}
                                                                isDisabled={true}
                                                            />

                                                            <FicInput
                                                                label="Numero de serie"
                                                                name="num_serie"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                            <FicInput
                                                                label="Modalidad"
                                                                name="modalidad"
                                                                control={control}
                                                                isDisabled={true}
                                                            />
                                                        </div>
                                                    </div>

                                                    <FicInput
                                                        componentStyle={{
                                                            marginTop: "15px",
                                                            marginBottom: "15px",
                                                        }}
                                                        control={control}
                                                        name="carro"
                                                        label="Carro"
                                                        isDisabled={true}
                                                    />

                                                    <div className="ventas-form__form-container">
                                                        <div style={{ color: "white" }}>
                                                            DATOS DE LA VENTA
                                                        </div>
                                                        <div className="ventas-form__inputs-container">
                                                            <FicDatePickerSI
                                                                control={control}
                                                                name="fecha_venta"
                                                                label="Fecha"
                                                            />
                                                            <FicComboBox
                                                                componentStyle={{ zIndex: "0" }}
                                                                control={control}
                                                                name="vendedor"
                                                                label="Vendedor"
                                                                loadOptions={listarVendedores}
                                                            />
                                                            <FicInput
                                                                label="Comprador"
                                                                name="comprador_nombre"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Numero de DPI"
                                                                name="comprador_dpi"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Domicilio fiscal"
                                                                name="comprador_domicilio"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Departamento"
                                                                name="comprador_departamento"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Profesion u oficio"
                                                                name="comprador_profesion"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Numero de Telefono"
                                                                name="comprador_num_telefono"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="NIT"
                                                                name="comprador_nit"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Edad"
                                                                name="comprador_edad"
                                                                control={control}
                                                            />
                                                            <FicInput
                                                                label="Estado civil"
                                                                name="comprador_estado_civil"
                                                                control={control}
                                                            />
                                                            <FicNumber
                                                                formatOptions={{
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                }}
                                                                label="Valor de venta"
                                                                name="valor_venta"
                                                                control={control}
                                                            />
                                                            <FicTextArea
                                                                label="Observaciones"
                                                                name="observaciones"
                                                                control={control}
                                                                style={{
                                                                    minHeight: "36.7px",
                                                                    gridColumn: "1/-1",
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </LoadMask>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabPane>
                            <TabPane tab="Póliza" key="POLIZA_TAB">
                                <div className="py-2 px-3">
                                    <div className="mb-4 mb-3">
                                        <div className="p-0 pt-3 d-flex flex-column flex-md-row">
                                            <div className="d-flex flex-column flex-1 mx-3">
                                                <LoadMask loading={loader} color="#bd0034" blur={true}>
                                                    <div className="ventas-form__form-container">
                                                        <Contenido>
                                                            <GenBtn
                                                                onPress={openModal}
                                                                BtnColor="#007bff"
                                                                style={{ marginTop: "15px", marginBottom: "15px" }}
                                                            >
                                                                Agregar Línea
                                                            </GenBtn>
                                                        </Contenido>
                                                        <TablaPolizas
                                                            datos = {poliza}
                                                            eliminarLinea = {eliminarLinea}
                                                            diferenciaTotal = {diferencia}
                                                            control={control}
                                                        />
                                                        <br/>
                                                        <div className='d-flex flex-row mt-3'>
                                                            <GenBtn
                                                                type="submit"
                                                                className="ventas-form__guardar-ingreso-btn"
                                                                style={{ marginTop: "15px" }}
                                                            >
                                                                Modificar Venta
                                                            </GenBtn>
                                                        </div>
                                                    </div>
                                                </LoadMask>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabPane>
                        </Tabs>
                    </form>
                    }
                    {(datos.contabilidad === 'N') &&
                        <form
                            encType="multipart/form-data"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <h3
                                className="p-2  title rounded shadow"
                                style={{
                                    background:
                                        "linear-gradient(to top right, #790021, #2d2d2d)",
                                }}
                            >
                                Modificar Venta
                            </h3>
                            <br />
                            <div className="d-flex flex-row justify-content-start mb-2">
                                <GenBtn
                                    BtnColor="#787878"
                                    style={{ width: "100px", height: "40px" }}
                                    onPress={(e) => {
                                        history.push(`/auto_ventas/${empresa}/ventas`);
                                    }}
                                >
                                    Regresar
                                </GenBtn>
                            </div>
                            <LoadMask loading={loader} color="#bd0034" blur={true}>
                                <div style={{ color: "white" }}>DATOS DEL VEHICULO</div>
                                <div className="ventas-form__auto-data">
                                    <div className="ventas-form__inputs-container">
                                        <FicInput
                                            label="Placa"
                                            name="placa"
                                            control={control}
                                            isDisabled={true}
                                        />
                                        <FicInput
                                            label="Codigo"
                                            name="codigo"
                                            control={control}
                                            isDisabled={true}
                                        />
                                        <FicInput
                                            label="Tipo de carro"
                                            name="tipo_carro"
                                            control={control}
                                            isDisabled={true}
                                        />
                                        <FicInput
                                            label="Modelo"
                                            name="modelo"
                                            control={control}
                                            isDisabled={true}
                                        />
                                        <FicInput
                                            label="Linea"
                                            name="linea"
                                            control={control}
                                            isDisabled={true}
                                        />

                                        <FicInput
                                            label="Numero de serie"
                                            name="num_serie"
                                            control={control}
                                            isDisabled={true}
                                        />
                                        <FicInput
                                            label="Modalidad"
                                            name="modalidad"
                                            control={control}
                                            isDisabled={true}
                                        />
                                    </div>
                                </div>

                                <FicInput
                                    componentStyle={{
                                        marginTop: "15px",
                                        marginBottom: "15px",
                                    }}
                                    control={control}
                                    name="carro"
                                    label="Carro"
                                    isDisabled={true}
                                />

                                <div className="ventas-form__form-container">
                                    <div style={{ color: "white" }}>
                                        DATOS DE LA VENTA
                                    </div>
                                    <div className="ventas-form__inputs-container">
                                        <FicDatePickerSI
                                            control={control}
                                            name="fecha_venta"
                                            label="Fecha"
                                        />
                                        <FicComboBox
                                            componentStyle={{ zIndex: "0" }}
                                            control={control}
                                            name="vendedor"
                                            label="Vendedor"
                                            loadOptions={listarVendedores}
                                        />
                                        <FicInput
                                            label="Comprador"
                                            name="comprador_nombre"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Numero de DPI"
                                            name="comprador_dpi"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Domicilio fiscal"
                                            name="comprador_domicilio"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Departamento"
                                            name="comprador_departamento"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Profesion u oficio"
                                            name="comprador_profesion"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Numero de Telefono"
                                            name="comprador_num_telefono"
                                            control={control}
                                        />
                                        <FicInput
                                            label="NIT"
                                            name="comprador_nit"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Edad"
                                            name="comprador_edad"
                                            control={control}
                                        />
                                        <FicInput
                                            label="Estado civil"
                                            name="comprador_estado_civil"
                                            control={control}
                                        />
                                        <FicNumber
                                            formatOptions={{
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }}
                                            label="Valor de venta"
                                            name="valor_venta"
                                            control={control}
                                        />
                                        <FicTextArea
                                            label="Observaciones"
                                            name="observaciones"
                                            control={control}
                                            style={{
                                                minHeight: "36.7px",
                                                gridColumn: "1/-1",
                                            }}
                                        />
                                    </div>
                                </div>
                                <GenBtn
                                    type="submit"
                                    className="ventas-form__guardar-ingreso-btn"
                                    style={{ marginTop: "15px" }}
                                >
                                Guardar
                                </GenBtn>
                            </LoadMask>
                        </form>
                        }
                    </div>
                </div>
            </div>    

            <DialogModal dialogRef={dialogRef} noHeader={true}>
                <DialogContent>
                    <div
                        style={{
                            fontSize: "20px",
                            fontWeight: "bold",
                            color: "#bd0034",
                        }}
                    >
                        Desea generar el finiquito?
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-around",
                            marginTop: "15px",
                            width: "100%",
                        }}
                    >
                        <GenBtn
                            textColor="hsl(4, 100%, 39%)"
                            background="hsla(4, 100%, 39%, 0.2)"
                            onPress={() => {
                                dialogRef.current.close();
                                setTimeout(() => {
                                    history.push(
                                        `/auto_ventas/${empresa}/ventas`
                                    );
                                }, 350);
                            }}
                        >
                            No
                        </GenBtn>
                        <GenBtn
                            onPress={() => {
                                reporteVentaDeVehiculos(ventaId);
                                setTimeout(() => {
                                    history.push(
                                        `/auto_ventas/${empresa}/ventas`
                                    );
                                }, 3000);
                            }}
                            autofocus="autofocus"
                        >
                            Generar
                        </GenBtn>
                    </div>
                </DialogContent>
            </DialogModal>

            <ModalOverlay isOpen={isModalOpen}>
                <ModalContent>
                <ModalHeader>
                    <ModalTitle>Ingresar detalle póliza</ModalTitle>
                    <CloseButton onClick={(e) => {
                        e.preventDefault(); 
                        closeModal();
                    }}>×</CloseButton>
                </ModalHeader>
                <hr style={{ borderColor: "white", margin: "0 0 20px 0" }} />
                                
                <form onSubmit={handleModalSubmit(agregarLinea)}>
                    <ModalForm>
                        <div style={{ gridColumn: "1 / 3" }}>
                            <FicComboBox
                            control={controlModal}
                            name="cuenta"
                            label="Cuenta contable..."
                            loadOptions={listarCuentas}
                            menuPlacement="top"
                            />
                        </div>
                    
                        <FicNumber
                            formatOptions={{
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            }}
                            label="Debe"
                            name="debe"
                            control={controlModal}
                        />
                    
                        <FicNumber
                            formatOptions={{
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            }}
                            label="Haber"
                            name="haber"
                            control={controlModal}
                        />
                    </ModalForm>
                    
                    <ModalFooter>
                        <DiffText value={diferencia}>
                            Diferencia: {Math.abs(diferencia).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </DiffText>
                        <GenBtn
                            type="button"
                            BtnColor="#6c757d"
                            onPress={closeModal}
                        >
                            Cancelar
                        </GenBtn>
                        <GenBtn
                            type="submit"
                            BtnColor="#007bff"
                        >
                            Agregar
                        </GenBtn>
                    </ModalFooter>
                </form>
                </ModalContent>
            </ModalOverlay>
        </React.Fragment>
    );
}

export default withRouter(VendedoresFormEditar);

const Contenido = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

// Componente Modal (mantiene los estilos oscuros)
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: ${props => props.isOpen ? 'flex' : 'none'};
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background-color: #2d2d2d;
    border-radius: 8px;
    padding: 20px;
    width: 80%;
    max-width: 900px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    color: #007bff;
`;

const ModalTitle = styled.h4`
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #007bff;
    &:hover {
        color: #0056b3;
    }
`;

const ModalForm = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    gap: 10px;
`;

const DiffText = styled.div`
    margin-right: auto;
    color: white;
`;